import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { useResource, titleCase, daysUntil } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { JOB_TITLES, companyCreateFields } from "../lib/formOptions";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { Company, Contact } from "../lib/types";

const KIND = [
    { value: "provider", label: "Provider" },
    { value: "plan_manager", label: "Plan manager" },
    { value: "support_coordinator", label: "Support coordinator" },
    { value: "referrer", label: "Referrer" },
    { value: "supplier", label: "Supplier" },
    { value: "other", label: "Other" },
];

type Tab = "organisations" | "contacts";

type Dialog =
    | { kind: "createCompany" }
    | { kind: "editCompany"; company: Company }
    | { kind: "deleteCompany"; company: Company }
    | { kind: "createContact"; companyId?: string }
    | { kind: "editContact"; contact: Contact }
    | { kind: "deleteContact"; contact: Contact }
    | null;

/**
 * One module, three doors. /providers shows both tabs; the CRM's Companies and
 * Contacts entries land on the same data locked to one tab, so there is a
 * single implementation to maintain and a single set of dialogs to test.
 */
export const Providers = ({ only }: { only?: Tab } = {}) => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const companies = useResource<Array<Company & { agreement_end_date?: string | null }>>("/companies");
    const contacts = useResource<Contact[]>("/contacts");
    // The organisations list wears the same stat strip as Participants —
    // providers are TrustCare's primary entity and the list should say at a
    // glance how the whole book of business is sitting.
    const cases = useResource<Array<{ id: string; company_id: string | null; status: string }>>(
        canManage ? "/cases" : null);
    const invoices = useResource<Array<{ id: string; company_id: string | null; status: string }>>(
        canManage ? "/invoices" : null);

    const [tab, setTab] = useState<Tab>(only ?? "organisations");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);

    const refresh = () => { companies.reload(); contacts.reload(); setDialog(null); };

    const companyName = useMemo(() => {
        const map = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
        return (id: string | null) => (id ? map.get(id) ?? "Unknown" : "—");
    }, [companies.data]);

    const companyFields: Field[] = [
        { name: "name", label: "Organisation name", kind: "text", required: true },
        { name: "kind", label: "Type", kind: "select", options: KIND, required: true, half: true },
        { name: "abn", label: "ABN", kind: "text", half: true,
          helpLink: { label: "Verify on ABN Lookup", href: "https://abr.business.gov.au/" } },
        { name: "ndis_registered", label: "NDIS registered provider", kind: "checkbox" },
        { name: "registration_id", label: "NDIS registration ID", kind: "text", half: true },
        { name: "primary_contact", label: "Primary contact", kind: "text", half: true },
        { name: "phone", label: "Phone", kind: "tel", half: true },
        { name: "email", label: "Email", kind: "email", half: true },
        { name: "billing_email", label: "Billing email", kind: "email", half: true,
          help: "Where this organisation's invoices are sent." },
        { name: "payment_terms_days", label: "Payment terms (days)", kind: "number", half: true, min: 0, max: 90 },
        { name: "agreement_start_date", label: "Agreement start", kind: "date", half: true,
          help: "The subcontractor agreement window with this provider." },
        { name: "agreement_end_date", label: "Agreement end", kind: "date", half: true },
        { name: "website", label: "Website", kind: "text", half: true },
        { name: "suburb", label: "Suburb", kind: "text", half: true },
        { name: "postcode", label: "Postcode", kind: "text", half: true },
        { name: "address", label: "Address", kind: "text" },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    const contactFields: Field[] = [
        { name: "first_name", label: "First name", kind: "text", required: true, half: true },
        { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
        { name: "job_title", label: "Job title", kind: "text", half: true, suggestions: JOB_TITLES },
        { name: "company_id", label: "Organisation", kind: "select", half: true,
          options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields() } },
        { name: "email", label: "Email", kind: "email", half: true },
        { name: "phone", label: "Phone", kind: "tel", half: true },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    const allCompanies = companies.data ?? [];
    const filteredCompanies = allCompanies.filter((c) =>
        matches(search, c.name, c.abn, c.suburb, c.primary_contact) &&
        (!filters.kind || c.kind === filters.kind) &&
        (!filters.registered || String(c.ndis_registered) === filters.registered)
    );

    const allContacts = contacts.data ?? [];
    const filteredContacts = allContacts.filter((c) =>
        matches(search, `${c.first_name} ${c.last_name}`, c.email, c.job_title, companyName(c.company_id)) &&
        (!filters.company_id || c.company_id === filters.company_id)
    );

    if (companies.error) return <div className="error-banner">{companies.error}</div>;

    return (
        <div>
            {dialog?.kind === "createCompany" && (
                <EntityForm title="Add Organisation" path="/companies" fields={companyFields}
                    subtitle="Providers, plan managers, support coordinators and referrers all live here, told apart by type."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "editCompany" && (
                <EntityForm title={`Edit ${dialog.company.name}`} path="/companies"
                    fields={companyFields} initial={dialog.company}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "deleteCompany" && (
                <ConfirmDialog
                    title={`Remove ${dialog.company.name}?`}
                    tone="danger"
                    message="The organisation is retired and disappears from the list."
                    warning="Contacts and referrals that point at it keep their link, so history stays readable. The record is retired, not erased."
                    confirmLabel="Remove organisation"
                    request={{ method: "delete", path: `/companies/${dialog.company.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "createContact" && (
                <EntityForm title="Add Contact" path="/contacts" fields={contactFields}
                    initial={dialog.companyId ? { company_id: dialog.companyId } : null}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "editContact" && (
                <EntityForm title="Edit contact" path="/contacts"
                    fields={contactFields} initial={dialog.contact}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "deleteContact" && (
                <ConfirmDialog
                    title={`Remove ${dialog.contact.first_name} ${dialog.contact.last_name}?`}
                    tone="danger"
                    message="The contact is retired from the list."
                    warning="Referrals recorded against this person keep their details, because the referral is a record of what was received at the time."
                    confirmLabel="Remove contact"
                    request={{ method: "delete", path: `/contacts/${dialog.contact.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            {!only && (
                <div className="tabs">
                    <button className={tab === "organisations" ? "active" : ""} onClick={() => { setTab("organisations"); setFilters({}); }}>
                        Organisations ({allCompanies.length})
                    </button>
                    <button className={tab === "contacts" ? "active" : ""} onClick={() => { setTab("contacts"); setFilters({}); }}>
                        Contacts ({allContacts.length})
                    </button>
                </div>
            )}

            {tab === "organisations" ? (
                <>
                    <div className="stat-strip">
                        <div className="stat good">
                            <div className="num">{allCompanies.length}</div>
                            <div className="lbl">Organisations</div>
                        </div>
                        <div className="stat good">
                            <div className="num">{allCompanies.filter((c) => c.ndis_registered).length}</div>
                            <div className="lbl">NDIS registered</div>
                        </div>
                        {(() => {
                            const expiring = allCompanies.filter((c) => {
                                if (!c.agreement_end_date) return false;
                                const days = daysUntil(c.agreement_end_date);
                                return days >= 0 && days < 60;
                            }).length;
                            return (
                                <div className={`stat ${expiring ? "warn" : "good"}`}>
                                    <div className="num">{expiring}</div>
                                    <div className="lbl">Agreements expiring &lt; 60 days</div>
                                </div>
                            );
                        })()}
                        {canManage && (
                            <>
                                <div className={`stat ${(cases.data ?? []).filter((c) => c.status === "open").length ? "warn" : "good"}`}>
                                    <div className="num">{(cases.data ?? []).filter((c) => c.status === "open").length}</div>
                                    <div className="lbl">Open cases</div>
                                </div>
                                <div className={`stat ${(invoices.data ?? []).filter((i) => i.status === "issued" || i.status === "sent").length ? "warn" : "good"}`}>
                                    <div className="num">{(invoices.data ?? []).filter((i) => i.status === "issued" || i.status === "sent").length}</div>
                                    <div className="lbl">Unpaid invoices</div>
                                </div>
                            </>
                        )}
                    </div>

                    <FilterBar
                        search={search} onSearch={setSearch}
                        searchPlaceholder="Search organisations…"
                        values={filters}
                        onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                        filters={[
                            { name: "kind", allLabel: "All Types", options: KIND },
                            { name: "registered", allLabel: "All Registration", options: [
                                { value: "true", label: "NDIS registered" },
                                { value: "false", label: "Not registered" },
                            ] },
                        ]}
                        resultNote={filteredCompanies.length !== allCompanies.length
                            ? `Showing ${filteredCompanies.length} of ${allCompanies.length} organisations` : undefined}
                        action={canManage && (
                            <button className="btn" onClick={() => setDialog({ kind: "createCompany" })}>+ Add Organisation</button>
                        )}
                    />

                    <div className="card">
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Organisation</th><th>Type</th><th>Contact</th>
                                            <th>Phone</th><th>NDIS</th>
                                            {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCompanies.map((company) => (
                                            <tr key={company.id}>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>
                                                        <Link to={`/providers/${company.id}`} title="Open the provider profile">
                                                            {company.name}
                                                        </Link>
                                                    </div>
                                                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                                        {company.suburb ?? ""}{company.abn ? ` · ABN ${company.abn}` : ""}
                                                    </div>
                                                </td>
                                                <td><span className="chip info">{titleCase(company.kind)}</span></td>
                                                <td>
                                                    <div>{company.primary_contact ?? "—"}</div>
                                                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{company.email ?? ""}</div>
                                                </td>
                                                <td className="mono">{company.phone ?? "—"}</td>
                                                <td>
                                                    {company.ndis_registered ? (
                                                        <>
                                                            <span className="chip pass">registered</span>
                                                            {company.registration_id && (
                                                                <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: 2 }}>
                                                                    {company.registration_id}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : <span className="chip mute">not registered</span>}
                                                </td>
                                                {canManage && (
                                                    <td>
                                                        <div className="row-actions">
                                                            <Link className="icon-btn" to={`/participants?provider=${company.id}`}>Participants</Link>
                                                            <Link className="icon-btn" to={`/crm/invoices?company=${company.id}`}>Invoices</Link>
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "editCompany", company })}>Edit</button>
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "createContact", companyId: company.id })}>+ Contact</button>
                                                            <button className="icon-btn danger" onClick={() => setDialog({ kind: "deleteCompany", company })}>Remove</button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {filteredCompanies.length === 0 && (
                                            <tr><td colSpan={canManage ? 6 : 5} className="empty">No organisations match.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <FilterBar
                        search={search} onSearch={setSearch}
                        searchPlaceholder="Search contacts…"
                        values={filters}
                        onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                        filters={[{
                            name: "company_id", allLabel: "All Organisations",
                            options: allCompanies.map((c) => ({ value: c.id, label: c.name })),
                        }]}
                        resultNote={filteredContacts.length !== allContacts.length
                            ? `Showing ${filteredContacts.length} of ${allContacts.length} contacts` : undefined}
                        action={canManage && (
                            <button className="btn" onClick={() => setDialog({ kind: "createContact" })}>+ Add Contact</button>
                        )}
                    />

                    <div className="card">
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Name</th><th>Job title</th><th>Organisation</th>
                                            <th>Email</th><th>Phone</th>
                                            {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredContacts.map((contact) => (
                                            <tr key={contact.id}>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                        <InitialsAvatar name={`${contact.first_name} ${contact.last_name}`} size={26} />
                                                        {contact.first_name} {contact.last_name}
                                                    </div>
                                                </td>
                                                <td>{contact.job_title ?? "—"}</td>
                                                <td>{companyName(contact.company_id)}</td>
                                                <td>{contact.email ?? "—"}</td>
                                                <td className="mono">{contact.phone ?? "—"}</td>
                                                {canManage && (
                                                    <td>
                                                        <div className="row-actions">
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "editContact", contact })}>Edit</button>
                                                            <button className="icon-btn danger" onClick={() => setDialog({ kind: "deleteContact", contact })}>Remove</button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {filteredContacts.length === 0 && (
                                            <tr><td colSpan={canManage ? 6 : 5} className="empty">No contacts match.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
